import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import { usePerms } from '@/lib/perms';
import { importsApi, type DiffPayload, type DiffRow, type ImportVersion } from '@/lib/imports';
import { aed, num } from '@/lib/format';

interface Props {
  version: ImportVersion;
  onVersion: (v: ImportVersion) => void;
  onBack: () => void;
  onPublished: (res: ImportVersion & { diff: DiffPayload; stock: { units: number; valueAed: number } }) => void;
  onError: (code: string) => void;
}

type DiffFilter = 'all' | 'new' | 'removed' | 'price' | 'stock';

/** Дифф с каталогом и публикация: гейты сервера (договор, проверка строк, второе согласование admin по риску ≥ 50 000 AED). */
export default function StepDiff({ version, onVersion, onBack, onPublished, onError }: Props) {
  const { t } = useTranslation();
  const { action } = usePerms();
  const [filter, setFilter] = useState<DiffFilter>('all');
  const [confirming, setConfirming] = useState(false);
  const [diff, setDiff] = useState<DiffPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setDiff(await importsApi.diff(version.id));
      setError(null);
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, [version.id]);
  useEffect(() => {
    void load();
  }, [load]);

  const diffRows = useMemo(() => diff?.rows ?? [], [diff]);
  const rows = useMemo(() => (filter === 'all' ? diffRows : diffRows.filter((r) => r.type === filter)), [filter, diffRows]);
  const counts = diff?.counts ?? { new: 0, removed: 0, price: 0, stock: 0, priceCuts: 0, priceRises: 0 };
  const state = diff?.state;

  const typeMeta: Record<string, { label: string; icon: string; tone: string }> = {
    new: { label: t('impDiff.typeNew'), icon: 'ri-add-circle-line', tone: 'text-primary-700 bg-primary-50 border-primary-200' },
    removed: { label: t('impDiff.typeRemoved'), icon: 'ri-subtract-line', tone: 'text-rose-700 bg-rose-50 border-rose-200' },
    price: { label: t('impDiff.typePrice'), icon: 'ri-price-tag-3-line', tone: 'text-accent-800 bg-accent-50 border-accent-200' },
    stock: { label: t('impDiff.typeStock'), icon: 'ri-archive-2-line', tone: 'text-secondary-800 bg-secondary-50 border-secondary-200' },
  };

  const renderValue = (row: DiffRow) => {
    const unit = row.unit === 'AED' ? aed : num;
    if (row.type === 'new') return <span className="tnum font-medium text-primary-700">+ {unit(row.newValue)}</span>;
    if (row.type === 'removed') return <span className="tnum font-medium text-rose-600">{t('impDiff.removedValue')}</span>;
    const up = row.newValue > row.oldValue;
    return (
      <div className="flex items-center justify-end gap-1.5">
        <span className="tnum text-[11.5px] text-foreground-500 line-through">{unit(row.oldValue)}</span>
        <i className={`ri-arrow-right-line text-[11px] ${up ? 'text-rose-500' : 'text-primary-600'}`} />
        <span className={`tnum font-semibold ${up ? 'text-rose-600' : 'text-primary-700'}`}>{unit(row.newValue)}</span>
      </div>
    );
  };

  const approve = async () => {
    setBusy(true);
    try {
      onVersion(await importsApi.approve(version.id));
      await load();
    } catch (e) {
      setError((e as ApiError).code);
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setConfirming(false);
    setBusy(true);
    try {
      onPublished(await importsApi.publish(version.id));
    } catch (e) {
      const code = (e as ApiError).code;
      setError(code);
      onError(code);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const blockers: string[] = [];
  if (state && !state.contractOk) blockers.push(t('impX.blockContract'));
  if (state && state.reviewPending > 0) blockers.push(t('impX.blockReview', { count: state.reviewPending }));
  if (state && state.missingPrice > 0) blockers.push(t('impX.blockPrice', { count: state.missingPrice }));
  if (state && state.secondApprovalRequired && !state.secondApproved) blockers.push(t('impX.blockApproval', { risk: Math.round(state.totalRiskAed).toLocaleString('en-AE') }));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: t('impDiff.statNew'), value: counts.new, icon: 'ri-add-circle-line', tone: 'text-primary-700' },
          { label: t('impDiff.statRemoved'), value: counts.removed, icon: 'ri-indeterminate-circle-line', tone: 'text-rose-600' },
          { label: t('impDiff.statPrice'), value: counts.price, icon: 'ri-price-tag-3-line', tone: 'text-accent-700' },
          { label: t('impDiff.statStock'), value: counts.stock, icon: 'ri-archive-2-line', tone: 'text-secondary-700' },
        ].map((s) => (
          <div key={s.label} className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div>
              <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-500">{s.label}</p>
              <p className="tnum mt-1 text-lg font-semibold text-foreground-950">{s.value}</p>
            </div>
            <i className={`${s.icon} text-[18px] ${s.tone}`} />
          </div>
        ))}
      </div>

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">
          {t(`impX.publishError.${error.split(':')[0]}`, { defaultValue: `${t('ui.error')}: ${error}` })}{' '}
          <button type="button" onClick={() => void load()} className="cursor-pointer font-semibold underline">
            {t('ui.retry')}
          </button>
        </p>
      )}

      {blockers.length > 0 && (
        <div className="rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-[12px] leading-5 text-accent-900">
          <p className="font-semibold">{t('impX.blockersTitle')}</p>
          <ul className="mt-1 list-disc pl-5">
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          {state?.secondApprovalRequired && !state.secondApproved && action('second_approval') && (
            <button type="button" disabled={busy} onClick={() => void approve()} className="mt-2 inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-accent-600 px-2.5 text-[11px] font-semibold text-background-50 hover:bg-accent-700 disabled:opacity-50">
              <i className="ri-shield-check-line text-[12px]" /> {t('impX.approveBtn')}
            </button>
          )}
        </div>
      )}
      {state?.secondApprovalRequired && state.secondApproved && (
        <p className="rounded-md border border-primary-200 bg-primary-50 px-4 py-2 text-[11.5px] text-primary-800">{t('impX.approved')}</p>
      )}

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-col gap-3 border-b border-background-200 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground-950">{t('impX.diffTitle', { version: version.version })}</h2>
            <p className="mt-0.5 text-[11.5px] text-foreground-500">{t('impDiff.sub', { count: diff?.items ?? version.itemsCount ?? 0 })}</p>
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-md border border-background-200 bg-background-100 p-1">
            {(['all', 'new', 'removed', 'price', 'stock'] as DiffFilter[]).map((f) => (
              <button key={f} type="button" onClick={() => setFilter(f)} className={`cursor-pointer whitespace-nowrap rounded px-2 py-1 text-[11px] font-medium transition-colors ${filter === f ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'}`}>
                {f === 'all' ? t('impDiff.allChanges') : typeMeta[f].label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-h-[440px] overflow-auto thin-scroll">
          <table className="w-full min-w-[720px] border-collapse text-[12px]">
            <thead className="sticky top-0 z-10 bg-background-100 text-left text-[10px] uppercase tracking-[0.07em] text-foreground-500">
              <tr className="border-b border-background-200">
                <th className="px-4 py-2 font-medium">{t('impDiff.colChange')}</th>
                <th className="px-3 py-2 font-medium">{t('impDiff.colSku')}</th>
                <th className="px-3 py-2 font-medium">{t('impDiff.colItem')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('impDiff.colValues')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const meta = typeMeta[row.type];
                return (
                  <tr key={`${row.sku}-${row.type}-${i}`} className="border-b border-background-200/70 last:border-0 hover:bg-background-100/60">
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-medium ${meta.tone}`}>
                        <i className={`${meta.icon} text-[10px]`} /> {meta.label}
                      </span>
                    </td>
                    <td className="tnum px-3 py-2 font-medium text-foreground-800">{row.sku}</td>
                    <td className="px-3 py-2 text-foreground-600">{row.name}</td>
                    <td className="px-4 py-2 text-right">{renderValue(row)}</td>
                  </tr>
                );
              })}
              {diff && rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-[12px] text-foreground-500">
                    {t('impDiff.empty')}
                  </td>
                </tr>
              )}
              {!diff && !error && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-[12px] text-foreground-400">
                    {t('ui.loading')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-background-200 bg-background-100/50 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-[12px] font-medium text-foreground-900">{t('impDiff.publishTitle', { count: diff?.items ?? version.itemsCount ?? 0, version: version.version })}</p>
            <p className="text-[11px] text-foreground-500">{t('impX.publishSub')}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button type="button" onClick={onBack} className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[11.5px] font-medium text-foreground-700 hover:bg-background-100">
              {t('impDiff.back')}
            </button>
            <button
              type="button"
              disabled={busy || !state?.canPublish}
              onClick={() => setConfirming(true)}
              className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-upload-2-line text-[13px]" /> {t('impDiff.publishBtn', { count: diff?.items ?? version.itemsCount ?? 0 })}
            </button>
          </div>
        </div>
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                <i className="ri-upload-2-line text-[17px]" />
              </span>
              <div>
                <h2 className="text-sm font-semibold text-foreground-950">{t('impDiff.confirmTitle', { version: version.version })}</h2>
                <p className="mt-1 text-[11.5px] leading-4 text-foreground-600">{t('impDiff.confirmBody', { count: diff?.items ?? 0, partner: version.partnerName ?? '' })}</p>
              </div>
            </div>
            <p className="mt-4 rounded-md border border-background-200 bg-background-100/60 p-2.5 text-[11.5px] leading-4 text-foreground-700">{t('impX.confirmNote')}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirming(false)} className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
                {t('impDiff.cancel')}
              </button>
              <button type="button" disabled={busy} onClick={() => void publish()} className="h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60">
                {busy ? t('ui.saving') : t('impDiff.publishNow')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
